import React, { useState } from "react";

import { fetchWithAuthentication } from "../utilities/FetchUtil";
import {
  Button,
  Center,
  Flex,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { isPasswordValid } from "../utilities/PasswordUtil";

interface registrationDetails {
  username: string;
  password: string;
  confirmPassword: string;
}

const Register = () => {
  const [registrationDetails, setRegistrationDetails] =
    useState<registrationDetails>({
      username: "",
      password: "",
      confirmPassword: "",
    });
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorText, setErrorText] = useState("Unable to register new user.");

  const handleUsernameInput = (input: string) => {
    setRegistrationDetails((prevState) => ({
      ...prevState,
      username: input,
    }));
  };

  const handlePasswordInput = (input: string) => {
    setRegistrationDetails((prevState) => ({
      ...prevState,
      password: input,
    }));
  };

  const handleConfirmPasswordInput = (input: string) => {
    setRegistrationDetails((prevState) => ({
      ...prevState,
      confirmPassword: input,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsError(false);
    setIsSuccess(false);
    if (!isPasswordValid(registrationDetails.password)) {
      setIsError(true);
      setErrorText(
        "Passwords must have 8-30 characters, with at least one lowercase letter, one uppercase letter, one digit, and one special character (!@#$%^&*).",
      );
      return;
    }
    if (registrationDetails.password !== registrationDetails.confirmPassword) {
      setIsError(true);
      setErrorText("Passwords do not match.");
      return;
    }
    try {
      const response = await fetchWithAuthentication(
        "/register",
        "POST",
        registrationDetails,
      );

      if (response.ok) {
        const data = await response.json();
        const { message } = data;
        setErrorText(message);
        setIsSuccess(true);
        setRegistrationDetails({
          username: "",
          password: "",
          confirmPassword: "",
        });
      } else if (response.status === 409) {
        setErrorText(
          "An admin user with the provided username already exists.",
        );
        setRegistrationDetails({
          username: "",
          password: "",
          confirmPassword: "",
        });
        setIsError(true);
      } else {
        const data = await response.json();
        const { message } = data;
        setErrorText(message);
        setIsError(true);
        setRegistrationDetails({
          username: "",
          password: "",
          confirmPassword: "",
        });
        console.error("Failed to register new user:", response.status);
      }
    } catch (error) {
      setErrorText("Something went wrong while trying to register a user.");
      setIsError(true);
      setRegistrationDetails({
        username: "",
        password: "",
        confirmPassword: "",
      });
      console.error("Error:", error);
    }
  };

  return (
    <Center h="75%">
      <Paper w={500} shadow="xl" p="xl" radius="md">
        <form onSubmit={handleSubmit}>
          <Flex direction="column" justify="center" gap="md" ta="center">
            <Title order={2} fw={500} className="login-header">
              Register a new user
            </Title>
            <Text>
              Please note that only a superuser can register new users.
            </Text>
            <Stack ta="left">
              <TextInput
                label="Username"
                required
                wrapperProps={{ display: "block" }}
                name="username"
                autoComplete="on"
                placeholder="Username"
                error={isError}
                value={registrationDetails.username}
                onChange={(e) => handleUsernameInput(e.target.value)}
              />
              <PasswordInput
                label="Password"
                required
                wrapperProps={{ display: "block" }}
                name="password"
                placeholder="Password"
                error={isError}
                autoComplete="on"
                value={registrationDetails.password}
                onChange={(e) => handlePasswordInput(e.target.value)}
              />
              <PasswordInput
                label="Confirm password"
                required
                wrapperProps={{ display: "block" }}
                name="confirmPassword"
                placeholder="Password"
                error={isError}
                autoComplete="on"
                value={registrationDetails.confirmPassword}
                onChange={(e) => handleConfirmPasswordInput(e.target.value)}
              />
            </Stack>
            <Center mih={30}>
              {isError && <Text c="red">{errorText}</Text>}
              {isSuccess && <Text c="green">{errorText}</Text>}
            </Center>
            <Button type="submit" className="login-button">
              <Title order={4}>Register</Title>
            </Button>
          </Flex>
        </form>
      </Paper>
    </Center>
  );
};

export default Register;
